import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import Navigation2 from './Navigation2';
import Footer from './Footer';
import './PricingPage.css';

const FeatureIcon = ({ type }) => {
  const icons = {
    mail: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M22 7L12 13L2 7" />
      </svg>
    ),
    scan: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
    search: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
    ),
    brain: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 01-2 2h-4a2 2 0 01-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z" />
        <path d="M9 21h6" />
        <path d="M10 17v4" />
        <path d="M14 17v4" />
      </svg>
    ),
    academy: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
        <path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5" />
      </svg>
    ),
    warmup: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2c1 3 4 5.5 4 10a4 4 0 11-8 0c0-4.5 3-7 4-10z" />
        <path d="M12 22v-4" />
      </svg>
    ),
    flow: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="6" height="5" rx="1" />
        <rect x="16" y="3" width="6" height="5" rx="1" />
        <rect x="9" y="16" width="6" height="5" rx="1" />
        <path d="M5 8v3a2 2 0 002 2h10a2 2 0 002-2V8" />
        <path d="M12 13v3" />
      </svg>
    ),
    account: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    compose: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
    api: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
        <line x1="14" y1="4" x2="10" y2="20" />
      </svg>
    ),
  };
  return <span className="pp-feature-icon">{icons[type]}</span>;
};

const CheckMark = () => (
  <svg className="pp-cmp-check" width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3.5 8.5L6.5 11.5L12.5 4.5" />
  </svg>
);

const DashMark = () => (
  <span className="pp-cmp-dash">-</span>
);

const CellValue = ({ value }) => {
  if (value === true) return <CheckMark />;
  if (value === false) return <DashMark />;
  return <span className="pp-cmp-text-value">{value}</span>;
};

const FeatureComparison = ({ t }) => {
  const [expandedCats, setExpandedCats] = useState(() =>
    new Set(t.compareCategories.map((_, i) => i))
  );

  const toggleCategory = (idx) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <section className="pp-compare-section">
      <div className="pp-compare-inner">
        <h2 className="pp-compare-title">{t.compareTitle}</h2>
        <p className="pp-compare-subtitle">{t.compareSubtitle}</p>

        <div className="pp-compare-table">
          <div className="pp-compare-header">
            <div className="pp-compare-header-label" />
            {t.comparePlans.map((plan, i) => (
              <div key={i} className={`pp-compare-header-plan ${i === 1 ? 'pp-compare-header-plan-popular' : ''}`}>
                {plan}
              </div>
            ))}
          </div>

          {t.compareCategories.map((cat, catIdx) => (
            <div key={catIdx} className="pp-compare-category">
              <button
                className="pp-compare-cat-header"
                onClick={() => toggleCategory(catIdx)}
              >
                <span className="pp-compare-cat-name">{cat.name}</span>
                <svg
                  className={`pp-compare-cat-chevron ${expandedCats.has(catIdx) ? 'pp-compare-cat-chevron-open' : ''}`}
                  width="14" height="14" viewBox="0 0 14 14"
                  fill="none" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"
                >
                  <path d="M3.5 5.5L7 9L10.5 5.5" />
                </svg>
              </button>

              <div className={`pp-compare-cat-body ${expandedCats.has(catIdx) ? 'pp-compare-cat-body-open' : ''}`}>
                {cat.features.map((feat, featIdx) => (
                  <div key={featIdx} className="pp-compare-row">
                    <div className="pp-compare-row-label">{feat.label}</div>
                    {feat.values.map((val, valIdx) => (
                      <div key={valIdx} className={`pp-compare-row-value ${valIdx === 1 ? 'pp-compare-row-value-popular' : ''}`}>
                        <CellValue value={val} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingPage = () => {
  const { language } = useLanguage();
  const t = translations[language].pricingPage;
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="pp-wrapper">
      <Navigation2
        actionLink="https://bonusclub.link"
        actionLinkName="Start for free"
      />

      <section className="pp-hero">
        <div className="pp-hero-glow pp-hero-glow-1" />
        <div className="pp-hero-glow pp-hero-glow-2" />
        <div className="pp-hero-inner">
          <span className="pp-label">{t.label}</span>
          <h1 className="pp-title">{t.title}</h1>
          <p className="pp-subtitle">{t.subtitle}</p>
        </div>
      </section>

      <section className="pp-cards-section">
        <div className="pp-cards-grid">
          {t.plans.map((plan, i) => (
            <div
              key={i}
              className={`pp-card ${plan.popular ? 'pp-card-popular' : ''}`}
            >
              {plan.popular && (
                <div className="pp-popular-badge">{plan.popularLabel}</div>
              )}

              <div className="pp-card-top">
                <h2 className="pp-card-name">{plan.name}</h2>
                <div className="pp-card-credits">
                  <span className="pp-credits-value">{plan.credits}</span>
                  <span className="pp-credits-label">{plan.creditsLabel}</span>
                </div>
              </div>

              <div className="pp-card-divider" />

              <p className="pp-card-desc">{plan.description}</p>

              <ul className="pp-feature-list">
                {plan.features.map((feat, j) => (
                  <li key={j} className={`pp-feature-row ${j < 3 ? 'pp-feature-row-primary' : ''}`}>
                    <FeatureIcon type={feat.icon} />
                    <span className="pp-feature-text">
                      <strong>{feat.bold}</strong>
                      {feat.text && <span className="pp-feature-light">{feat.text}</span>}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                className="pp-more-hint"
                onClick={() => document.querySelector('.pp-compare-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t.moreHint}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 3v8M3.5 7.5L7 11l3.5-3.5" />
                </svg>
              </button>

              <a
                href={plan.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`pp-card-cta ${plan.popular ? 'pp-card-cta-primary' : ''}`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        <p className="pp-footnote">{t.footnote}</p>
      </section>

      <section className="pp-hyper-section">
        <div className="pp-hyper-inner">
          <div className="pp-hyper-glow" />
          <span className="pp-hyper-badge">{t.hyperBadge}</span>
          <p className="pp-hyper-desc">{t.hyperDesc}</p>
        </div>
      </section>

      <FeatureComparison t={t} />

      <section className="pp-faq-section">
        <h2 className="pp-faq-title">{t.faqTitle}</h2>
        <div className="pp-faq-list">
          {t.faqs.map((faq, i) => (
            <div
              key={i}
              className={`pp-faq-item ${openFaq === i ? 'pp-faq-open' : ''}`}
            >
              <button
                className="pp-faq-question"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{faq.q}</span>
                <svg
                  className={`pp-faq-chevron ${openFaq === i ? 'pp-faq-chevron-open' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 6l4 4 4-4" />
                </svg>
              </button>
              <div className="pp-faq-answer">
                <p>{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
